import React, { useEffect, useRef, useState } from "react";
import { getDocument } from "pdfjs-dist";
import { setPdfWorker } from "../../../utils/pdfWorkerConfig";
import { Box, Stack, IconButton, Tooltip, Typography } from "@mui/material";

setPdfWorker();

const PdfViewer = ({ pdfUrl, scrollable = false }) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const canvasRefs = useRef({}); // Store refs for all pages
  const renderTaskRefs = useRef({}); // Track render tasks for each page

  const fetchPdfBlob = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch PDF");
      return await response.arrayBuffer();
    } catch (error) {
      console.error("Error fetching PDF:", error);
      return null;
    }
  };

  useEffect(() => {
    const loadPdf = async () => {
      const arrayBuffer = await fetchPdfBlob(pdfUrl);
      if (arrayBuffer) {
        const pdf = await getDocument({ data: arrayBuffer }).promise;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
      }
    };
    if (pdfUrl) loadPdf();
  }, [pdfUrl]);

  // Render single page (for non-scrollable mode)
  useEffect(() => {
    if (!scrollable && pdfDoc && pageNum) {
      const renderPage = async (num) => {
        const page = await pdfDoc.getPage(num);
        const canvas = canvasRefs.current[num] || canvasRefs.current["single"];
        const context = canvas.getContext("2d");
        const viewport = page.getViewport({ scale: 1.5 });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        if (renderTaskRefs.current[num]) renderTaskRefs.current[num].cancel();

        const renderTask = page.render({ canvasContext: context, viewport });
        renderTaskRefs.current[num] = renderTask;

        try {
          await renderTask.promise;
          page.cleanup();
        } catch (err) {
          console.error("Render canceled:", err);
        }
      };
      renderPage(pageNum);
    }
  }, [pdfDoc, pageNum, scrollable]);

  // Render all pages if scrollable
  useEffect(() => {
    if (scrollable && pdfDoc) {
      const renderAllPages = async () => {
        for (let i = 1; i <= pdfDoc.numPages; i++) {
          const page = await pdfDoc.getPage(i);
          const canvas =
            canvasRefs.current[i] || document.createElement("canvas");
          canvasRefs.current[i] = canvas;
          const context = canvas.getContext("2d");
          const viewport = page.getViewport({ scale: 1.5 });

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          const renderTask = page.render({ canvasContext: context, viewport });
          renderTaskRefs.current[i] = renderTask;

          try {
            await renderTask.promise;
            page.cleanup();
          } catch (err) {
            console.error(`Render canceled for page ${i}:`, err);
          }
        }
      };
      renderAllPages();
    }
  }, [pdfDoc, scrollable]);

  if (!scrollable) {
    // Single-page mode
    const goToPreviousPage = () => pageNum > 1 && setPageNum(pageNum - 1);
    const goToNextPage = () => pageNum < numPages && setPageNum(pageNum + 1);

    return (
      <Box
        sx={{
          maxHeight: "100vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction={"row"}
          spacing={2}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Tooltip title="Previous page">
            <span>
              <IconButton
                onClick={goToPreviousPage}
                disabled={pageNum <= 1}
                size="small"
              >
                <Typography variant="h6">{`<`}</Typography>
              </IconButton>
            </span>
          </Tooltip>
          <Typography variant="body2">
            Page {pageNum} of {numPages || "-"}
          </Typography>
          <Tooltip title="Next page">
            <span>
              <IconButton
                onClick={goToNextPage}
                disabled={pageNum >= numPages}
                size="small"
              >
                <Typography variant="h6">{`>`}</Typography>
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <canvas ref={(el) => (canvasRefs.current["single"] = el)}></canvas>
      </Box>
    );
  }

  // Scrollable mode
  return (
    <Box
      sx={{
        maxHeight: "80vh",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {Array.from({ length: numPages || 0 }, (_, i) => (
        <canvas key={i + 1} ref={(el) => (canvasRefs.current[i + 1] = el)} />
      ))}
    </Box>
  );
};

export default PdfViewer;
